import { Box, InputBase, NativeSelect, Typography } from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import TablePagination, { TablePaginationProps } from '@mui/material/TablePagination';
import { map, range, startCase, toNumber } from 'lodash';
import React from 'react';

import { DataGridPagination } from 'components/atoms/DataGridPagination';

export type PaginationControlsMode = 'standard' | 'data-grid' | 'table';
export type PaginationControlsPageChangeSource = 'pagination' | 'jump' | 'data-grid' | 'table';
type PaginationControlsEvent =
  | React.ChangeEvent<unknown>
  | React.ChangeEvent<HTMLSelectElement>
  | React.MouseEvent<HTMLButtonElement, MouseEvent>;

export interface PaginationControlsPageChange {
  event: PaginationControlsEvent;
  page: number;
  source: PaginationControlsPageChangeSource;
}

export interface PaginationControlsRowsPerPageChange {
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
  pageSize: number;
}

export interface PaginationControlsProps {
  totalRows: number;
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  entityLabel: string;
  isLoading?: boolean;
  mode?: PaginationControlsMode;
  hideRowsPerPage?: boolean;
  alignRowsPerPageLeft?: boolean;
  onPageChange?: (params: PaginationControlsPageChange) => void;
  onRowsPerPageChange?: (params: PaginationControlsRowsPerPageChange) => void;
}

const buildDisplayedRowsLabel = ({
  from,
  to,
  count,
  isLoading,
  entityLabel,
}: {
  from: number;
  to: number;
  count: number;
  isLoading?: boolean;
  entityLabel: string;
}) => {
  if (isLoading && count === 0) {
    return '';
  }

  if (count === 0) {
    return `No ${entityLabel}`;
  }

  return `${from}-${to} of ${count} ${entityLabel}`;
};

const PaginationControlsTableActions = ({
  page,
  onPageChange,
  className,
  count,
  rowsPerPage,
}: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className' | 'count' | 'rowsPerPage'>) => {
  const pageCount = count === 0 ? 0 : Math.ceil(count / rowsPerPage);

  return (
    <MuiPagination
      color="primary"
      className={className}
      count={pageCount}
      page={page + 1}
      size="small"
      siblingCount={0}
      onChange={(event, nextPage) => {
        onPageChange?.(event as React.MouseEvent<HTMLButtonElement, MouseEvent>, nextPage - 1);
      }}
    />
  );
};

export const PaginationControls: React.FC<React.PropsWithChildren<PaginationControlsProps>> = ({
  totalRows,
  page,
  pageSize,
  pageSizeOptions,
  entityLabel,
  isLoading = false,
  mode = 'standard',
  hideRowsPerPage = false,
  alignRowsPerPageLeft = false,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const onPageChangeRef = React.useRef(onPageChange);
  const onRowsPerPageChangeRef = React.useRef(onRowsPerPageChange);
  const currentPageRef = React.useRef(page);
  const currentPageSizeRef = React.useRef(pageSize);

  onPageChangeRef.current = onPageChange;
  onRowsPerPageChangeRef.current = onRowsPerPageChange;
  currentPageRef.current = page;
  currentPageSizeRef.current = pageSize;

  const currentRowsPerPage = Math.max(currentPageSizeRef.current, 1);
  const currentPageNumber = Math.max(currentPageRef.current, 1);
  const currentPage = currentPageNumber - 1;
  const pageCount = totalRows === 0 ? 0 : Math.ceil(totalRows / currentRowsPerPage);

  const emitPageChange = React.useCallback(
    (event: PaginationControlsEvent, nextPage: number, source: PaginationControlsPageChangeSource) => {
      if (nextPage < 1 || nextPage === currentPageRef.current) {
        return;
      }

      onPageChangeRef.current?.({
        event,
        page: nextPage,
        source,
      });
    },
    []
  );

  const handleDataGridPageChange = React.useCallback(
    (event: React.ChangeEvent<unknown>, nextPage?: number) => {
      if (nextPage == null) {
        return;
      }

      emitPageChange(event, nextPage + 1, 'data-grid');
    },
    [emitPageChange]
  );

  const handleTablePageChange = React.useCallback<NonNullable<TablePaginationProps['onPageChange']>>(
    (event, nextPage) => {
      emitPageChange((event ?? {}) as React.MouseEvent<HTMLButtonElement, MouseEvent>, nextPage + 1, 'table');
    },
    [emitPageChange]
  );

  const handleRowsPerPageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newPageSize = toNumber(event.target.value) || currentPageSizeRef.current;

      if (newPageSize === currentPageSizeRef.current) {
        return;
      }

      onRowsPerPageChangeRef.current?.({
        event,
        pageSize: newPageSize,
      });
    },
    []
  );

  const handleDirectPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPage = toNumber(event.target.value);

    if (!selectedPage) {
      return;
    }

    emitPageChange(event, selectedPage, 'jump');
  };

  const labelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) =>
    buildDisplayedRowsLabel({ from, to, count, isLoading, entityLabel });
  const labelPrefix = startCase(entityLabel);
  const rowsPerPageLabel = `${labelPrefix} per page:`;
  const rowsPerPageAriaLabel = `${labelPrefix} per page`;
  const paginationSx = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    '&>.MuiTablePagination-toolbar': {
      width: hideRowsPerPage || alignRowsPerPageLeft ? '100%' : 'max-content',
      paddingLeft: hideRowsPerPage ? 0 : { xs: 0.5, xl: 1 },
      paddingRight: { xs: 0.5, xl: 1 },
      minHeight: 40,
      flexWrap: 'nowrap',
      columnGap: { xs: 0.5, xl: 1 },
      '& .MuiInputBase-root': {
        width: 'max-content',
        fontSize: { xs: '0.75rem', xl: '0.875rem' },
      },
      '&>.MuiToolbar-root': {
        alignItems: 'center',
        marginBottom: 0,
      },
      ...(alignRowsPerPageLeft
        ? {
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
          }
        : {}),
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-input': {
        display: hideRowsPerPage ? 'none' : undefined,
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-input, & .MuiTablePagination-displayedRows':
        {
          fontSize: { xs: '0.75rem', xl: '0.875rem' },
          lineHeight: 1.2,
          margin: 0,
          whiteSpace: 'nowrap',
        },
      '& .MuiTablePagination-displayedRows': {
        minWidth: hideRowsPerPage ? 'auto' : { xs: '72px', xl: '90px' },
        margin: alignRowsPerPageLeft ? '0 0 0 auto' : 0,
        whiteSpace: 'nowrap',
      },
      '& .MuiTablePagination-actions': {
        marginLeft: { xs: 0.5, xl: 1 },
      },
      '& .MuiPagination-ul': {
        flexWrap: 'nowrap',
      },
      '& .MuiPaginationItem-root': {
        minWidth: { xs: 26, xl: 32 },
        height: { xs: 26, xl: 32 },
        margin: { xs: '0 1px', xl: '0 2px' },
        fontSize: { xs: '0.75rem', xl: '0.875rem' },
      },
    },
  };

  return mode === 'data-grid' ? (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <DataGridPagination
        rowsPerPageOptions={hideRowsPerPage ? [] : pageSizeOptions}
        count={totalRows}
        rowsPerPage={currentRowsPerPage}
        page={currentPage}
        onPageChange={handleDataGridPageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sx={paginationSx}
        labelRowsPerPage={rowsPerPageLabel}
        labelDisplayedRows={labelDisplayedRows}
        slotProps={{
          select: {
            inputProps: {
              'aria-label': rowsPerPageAriaLabel,
            },
            native: true,
          },
        }}
      />
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Page
          </Typography>
          <NativeSelect
            value={currentPageNumber}
            onChange={handleDirectPageChange}
            input={<InputBase />}
            inputProps={{
              'aria-label': 'Jump to page',
            }}
          >
            {map(range(1, pageCount + 1), (pageOption) => (
              <option key={pageOption} value={pageOption}>
                {pageOption}
              </option>
            ))}
          </NativeSelect>
        </Box>
      )}
    </Box>
  ) : mode === 'table' ? (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <TablePagination
        component="div"
        rowsPerPageOptions={hideRowsPerPage ? [] : pageSizeOptions}
        count={totalRows}
        rowsPerPage={currentRowsPerPage}
        page={currentPage}
        onPageChange={handleTablePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        ActionsComponent={PaginationControlsTableActions}
        sx={paginationSx}
        labelRowsPerPage={rowsPerPageLabel}
        labelDisplayedRows={labelDisplayedRows}
        slotProps={{
          select: {
            inputProps: {
              'aria-label': rowsPerPageAriaLabel,
            },
            native: true,
          },
        }}
      />
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Page
          </Typography>
          <NativeSelect
            value={currentPageNumber}
            onChange={handleDirectPageChange}
            input={<InputBase />}
            inputProps={{
              'aria-label': 'Jump to page',
            }}
          >
            {map(range(1, pageCount + 1), (pageOption) => (
              <option key={pageOption} value={pageOption}>
                {pageOption}
              </option>
            ))}
          </NativeSelect>
        </Box>
      )}
    </Box>
  ) : (
    <MuiPagination
      shape="rounded"
      disabled={pageCount <= 1}
      count={pageCount}
      onChange={(event, nextPage) => {
        emitPageChange(event as React.ChangeEvent<unknown>, nextPage, 'pagination');
      }}
      page={currentPageNumber}
    />
  );
};
